import React from 'react';

import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';

import HandsonArticleLayout from './components/HandsonArticleLayout';
import WorkflowChatBubble from './components/WorkflowChatBubble';
import WorkflowChecklist from './components/WorkflowChecklist';
import WorkflowCodeBlock from './components/WorkflowCodeBlock';
import WorkflowDirectoryTree from './components/WorkflowDirectoryTree';
import WorkflowGoalBox from './components/WorkflowGoalBox';
import WorkflowInfoBox from './components/WorkflowInfoBox';
import WorkflowLinkCard from './components/WorkflowLinkCard';
import WorkflowList from './components/WorkflowList';
import WorkflowLockedCodeBlock from './components/WorkflowLockedCodeBlock';
import WorkflowSection from './components/WorkflowSection';
import WorkflowTable from './components/WorkflowTable';
import WorkflowTableOfContents from './components/WorkflowTableOfContents';
import WorkflowText from './components/WorkflowText';
import WorkflowTipBox from './components/WorkflowTipBox';

const seriesSteps = [
  {
    label: 'プロジェクト作成',
    title: 'プロジェクト作成 + Steeringルール設置',
    path: '/devdocs/handson/project-setup',
  },
  { label: 'API仕様書取込', title: 'API仕様書の取り込み', active: true },
  {
    label: '画面入出力定義書',
    title: '画面入出力定義書の作成',
    path: '/devdocs/handson/screen-io',
  },
  { label: '画面実装', title: '画面実装', path: '/devdocs/handson/implementation' },
];

const HandsonApiSpecs: React.FC = () => {
  return (
    <HandsonArticleLayout
      title="フロントエンド編 — API仕様書の取り込み"
      seriesName="フロントエンド編"
      tags={['Hands-on', 'Frontend', 'API Specs', 'Swagger', 'OpenAPI']}
      seriesSteps={seriesSteps}
      prevLink={{
        label: 'プロジェクト作成 + Steeringルール設置',
        path: '/devdocs/handson/project-setup',
      }}
      nextLink={{
        label: '画面入出力定義書の作成',
        path: '/devdocs/handson/screen-io',
      }}
    >
      <WorkflowGoalBox
        prerequisites={[
          '「フロントエンド編 — プロジェクト作成 + Steeringルール設置」が完了している',
          'バックエンドプロジェクト（team-todo）が同じマシン上に存在する',
        ]}
        completionCriteria={[
          '.kiro/steering/api_specs_rule.md が作成されている',
          'docs/api_specs.yaml がAIによって生成されている',
          '生成されたYAMLがOpenAPI 3.0形式である',
          'セクション7のチェックリスト全項目を確認している',
        ]}
      >
        バックエンドのAPI仕様書を「フロントが使いやすい形」に変換し、Swagger（OpenAPI）形式のapi_specs.yamlを生成する。
      </WorkflowGoalBox>

      <WorkflowLinkCard
        title="フロントエンド編 — プロジェクト作成 + Steeringルール設置"
        description="前の記事"
        path="/devdocs/handson/project-setup"
      />

      <WorkflowTableOfContents
        items={[
          { id: 'section-1', label: 'なぜフロント側で改めてAPI仕様書を作るのか' },
          { id: 'section-2', label: 'Swagger（OpenAPI）とは' },
          { id: 'section-3', label: 'Steeringルールを設置する' },
          { id: 'section-4', label: 'AIでapi_specs.yamlを生成する' },
          { id: 'section-5', label: '生成結果を確認する' },
          { id: 'section-6', label: '現在のプロジェクト構成' },
        ]}
      />

      {/* セクション1: なぜフロント側で改めてAPI仕様書を作るのか */}
      <WorkflowSection title="なぜフロント側で改めてAPI仕様書を作るのか" id="section-1" number={1}>
        <WorkflowText>
          バックエンドの実装編で、すでにAPI仕様書を作りました。「それをそのまま使えばいいのでは？」と思うかもしれません。
        </WorkflowText>
        <WorkflowText>
          結論から言うと、バックエンドの仕様書はフロントが欲しい情報を満たしていません。だからフロント側で「自分に都合の良い形」に作り直します。
        </WorkflowText>

        <WorkflowSection title="バックエンドの仕様書は「実装者向け」" subNumber="1.1">
          <WorkflowText>
            バックエンドのAPI仕様書は、バックエンドの実装に必要な粒度で書かれています。たとえば「処理概要」には「todoテーブルにINSERTする」「存在チェックで404を返す」といった内容が並びます。
          </WorkflowText>
          <WorkflowText>一方、フロントエンドが知りたいのは:</WorkflowText>
          <WorkflowList
            items={[
              'このAPIにどんなフィールドを送れば良いのか',
              'レスポンスのどのフィールドを画面に表示すればいいのか',
              'エラーのとき画面にどんなメッセージを出すべきか',
            ]}
          />
          <WorkflowText>
            これらは「画面を作る人の視点」であり、バックエンドの仕様書が想定する読者とは違います。
          </WorkflowText>
        </WorkflowSection>

        <WorkflowSection title="具体例: バックエンドの仕様書だけだと困る場面" subNumber="1.2">
          <WorkflowTable
            headers={['#', '場面', 'なぜ困るか']}
            rows={[
              [
                '1',
                'ステータスコードの一覧だけ書かれている',
                '「400のときユーザーに何を表示すればいいのか」が分からない。バリデーションエラーのフィールド名が返ってくるのか、メッセージが返ってくるのか',
              ],
              [
                '2',
                'Requestの型がスキーマ定義と対応関係で書かれている',
                'フロントが送るJSON構造を一目で把握できない。スキーマ定義を別途読みに行く必要がある',
              ],
              [
                '3',
                '仕様書の最終更新日が分からない',
                '実装が進んで仕様変更があった場合、バックエンド側の仕様書が更新されているか確認する手段がない',
              ],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="だから「フロント向け」に作り直す" subNumber="1.3">
          <WorkflowText>フロント側で改めてAPI仕様書を作り直す理由を整理すると:</WorkflowText>
          <WorkflowList
            items={[
              'フロントが使う情報だけを1ファイルにまとめる — スキーマ定義やDD書を横断して読む必要がなくなる',
              '標準フォーマット（OpenAPI）で書く — AIがフォーマットを知っているので、ルールで細かく指示しなくてもブレない',
              '自分のリポジトリで管理する — バックエンドの変更に気づかなくても、自分の仕様書が「現時点の前提」として機能する',
            ]}
          />

          <WorkflowInfoBox type="info">
            <Typography variant="body2" fontWeight={600}>
              ポイント
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              「作り直す」と言っても全部手書きするわけではありません。バックエンドの仕様書を入力としてAIに変換してもらいます。あなたがやることはSteeringルールを1つ設置して、プロンプトを1回入力するだけです。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション2: Swagger（OpenAPI）とは */}
      <WorkflowSection title="Swagger（OpenAPI）とは" id="section-2" number={2}>
        <WorkflowText>
          Swagger（正式名称: OpenAPI Specification）は、REST
          APIの仕様を記述するための標準フォーマットです。YAML（またはJSON）で書きます。
        </WorkflowText>

        <WorkflowCodeBlock
          language="yaml"
          title="OpenAPI形式の例"
          code={`# OpenAPI形式の例
paths:
  /api/todos:
    post:
      summary: TODO登録
      requestBody:
        content:
          application/json:
            schema:
              type: object
              properties:
                title:
                  type: string
      responses:
        '200':
          description: 成功`}
        />

        <WorkflowSection title="なぜSwaggerを採用するのか" subNumber="2.1">
          <WorkflowText>
            AIにとって、MarkdownよりもYAMLやJSONのような構造化データの方が解釈精度が高くなります。スキーマの型やネスト構造を曖昧さなく表現できるためです。
          </WorkflowText>
          <WorkflowText>
            一方で、YAMLやJSONは人間が目で確認するには不向きです。数百行のYAMLを上から下まで読んで「このAPIのレスポンスは何か」を把握するのは辛い作業です。
          </WorkflowText>
          <WorkflowText>
            OpenAPI（Swagger）はこのジレンマを解決します。データ自体はYAML/JSONの構造体でありながら、Swagger
            UIという標準の可視化ツールが用意されており、ブラウザ上でAPIの一覧・リクエスト・レスポンスを視覚的に確認できます。
          </WorkflowText>
          <WorkflowText>
            つまり、AIが正確に読み書きでき、かつ人間も見やすい — この両立がSwagger採用の理由です。
          </WorkflowText>
        </WorkflowSection>

        <WorkflowTipBox title="Swaggerの詳しい仕様を知りたい場合">
          <WorkflowText>OpenAPI Specificationの公式ドキュメントはこちらです:</WorkflowText>
          <WorkflowList
            items={[
              <Typography variant="body2" color="text.secondary" key="1">
                <Link href="https://swagger.io/specification/" target="_blank" rel="noopener">
                  OpenAPI Specification（公式）
                </Link>
              </Typography>,
              <Typography variant="body2" color="text.secondary" key="2">
                <Link href="https://editor.swagger.io/" target="_blank" rel="noopener">
                  Swagger Editor（ブラウザで試せる）
                </Link>
              </Typography>,
            ]}
          />
          <WorkflowText>
            このハンズオンでは仕様を細かく理解する必要はありません。AIが正しいフォーマットで出力してくれるので、「こういうものがあるんだな」程度の理解で十分です。
          </WorkflowText>
        </WorkflowTipBox>
      </WorkflowSection>

      {/* セクション3: Steeringルールを設置する */}
      <WorkflowSection title="Steeringルールを設置する" id="section-3" number={3}>
        <WorkflowText>
          バックエンドのAPI仕様書を入力として、OpenAPI形式のYAMLを生成するためのSteeringルールを設置します。
        </WorkflowText>

        <WorkflowSection title="何を書くか" subNumber="3.1">
          <WorkflowText>
            このルールに書くのは「フォーマットの指示」ではなく、情報ソースの場所と優先順位です。Swaggerのフォーマット自体はAIが知っているので指示不要。代わりに「どこを見て情報を集めればよいか」を正確に伝えます。
          </WorkflowText>
        </WorkflowSection>

        <WorkflowSection title="完成例をコピペする" subNumber="3.2">
          <WorkflowText>
            以下の完成例をコピーして .kiro/steering/api_specs_rule.md として保存してください。
          </WorkflowText>

          <WorkflowInfoBox type="important">
            <Typography variant="body2">
              <strong>重要</strong>: 完成例の中に {'{あなたのバックエンドプロジェクトの絶対パス}'}{' '}
              という箇所があります。ここを自分の環境に合わせて書き換えてください。例:
              /home/user/projects/team-todo や /Users/username/workspace/team-todo
            </Typography>
          </WorkflowInfoBox>

          <WorkflowLockedCodeBlock password="Kv9pTe" showPassword>
            {`---
inclusion: manual
---

# API仕様書生成ルール（フロントエンド向け）

## 目的

バックエンドのAPI仕様書・DD書・スキーマ定義を入力として、
フロントエンドが利用するOpenAPI 3.0形式のAPI仕様書（Swagger）を生成する。

## 出力先

- \`docs/api_specs.yaml\`

## 出力フォーマット

- OpenAPI 3.0（YAML形式）
- 全APIを1ファイルにまとめる
- 日本語でdescriptionを記述する

## 情報ソース

バックエンドプロジェクトの以下の資材を参照する。

### バックエンドプロジェクトのパス

\`\`\`
{あなたのバックエンドプロジェクトの絶対パス}
\`\`\`

### プロジェクト構成の参照

バックエンドプロジェクトの \`.kiro/steering/structure.md\` を読み込み、
各ドキュメントの配置先を把握した上で情報を収集すること。

### 情報ソースの優先順位

情報に矛盾がある場合は、以下の優先順位で採用する:

1. **API仕様書**（\`docs/api/\` 配下）— Request/Response/エラーの定義
2. **DD書**（存在する場合）— 処理の詳細・バリデーションルール
3. **スキーマ定義**（\`docs/schema/\` 配下）— テーブル構造・カラム型

### 収集する情報

各APIについて以下の情報を収集し、OpenAPI形式に変換する:

| 情報 | 参照先 | OpenAPIでの出力先 |
|------|--------|------------------|
| エンドポイント・メソッド | API仕様書の「概要」 | paths のキー |
| リクエストボディ | API仕様書の「Request > Body」 | requestBody.content.application/json.schema |
| パスパラメータ | API仕様書の「Request > パスパラメータ」 | parameters（in: path） |
| レスポンスボディ | API仕様書の「Response > Body」 | responses.200.content.application/json.schema |
| ステータスコード | API仕様書の「Response > Status Code」 | responses の各コード |
| 処理概要 | API仕様書の「処理概要」 | description |

## 変換ルール

### 型マッピング

| API仕様書の型 | OpenAPIの型 |
|--------------|-------------|
| string | type: string |
| int | type: integer, format: int32 |
| long | type: integer, format: int64 |
| boolean | type: boolean |
| datetime | type: string, format: date-time |
| object | type: object + properties定義 |
| object[] | type: array + items定義 |

### レスポンス構造

- 1件取得API: \`result\` オブジェクトでラップされている前提で定義する
- 複数件取得API: \`results\` 配列でラップされている前提で定義する

### エラーレスポンス

- 400（バリデーションエラー）/ 404（対象なし）/ 500（サーバーエラー）を共通定義する
- components.schemas にエラーレスポンスの共通スキーマを定義する

## 禁止事項

- API仕様書に記載のないAPIを追加しない
- フィールドの型をAPI仕様書と異なるものに変更しない
- 独自のフィールドを追加しない`}
          </WorkflowLockedCodeBlock>
        </WorkflowSection>

        <WorkflowSection title="パスの書き換え" subNumber="3.3">
          <WorkflowText>
            完成例の以下の箇所を、あなたの環境に合わせて書き換えてください。
          </WorkflowText>

          <WorkflowCodeBlock
            language="text"
            code={`{あなたのバックエンドプロジェクトの絶対パス}`}
          />

          <WorkflowText>例:</WorkflowText>
          <WorkflowList
            items={[
              'macOS: /Users/yourname/workspace/team-todo',
              'Linux: /home/yourname/projects/team-todo',
              'Windows (WSL): /mnt/c/Users/yourname/workspace/team-todo',
            ]}
          />

          <WorkflowTipBox title="なぜ絶対パスで指定するのか？">
            <WorkflowText>
              バックエンドとフロントエンドは別のリポジトリ（別のワークスペース）です。Steeringルール内で相対パス（../team-todo/...）を書いても、Kiroはワークスペースの外にあるファイルを相対パスで解決できません。
            </WorkflowText>
            <WorkflowText>
              絶対パスを指定することで、AIがバックエンドのファイルを正しく参照できるようになります。
            </WorkflowText>
          </WorkflowTipBox>

          <WorkflowTipBox title="なぜmanual読み込みなのか？">
            <WorkflowText>
              このルールは「api_specs.yamlを生成するとき」だけ使います。普段のコーディング中に毎回読み込まれる必要はありません。
            </WorkflowText>
            <WorkflowText>
              読み込みモードを manual にしておくことで、チャットで #api_specs_rule
              と明示的に指定したときだけルールが適用されます。
            </WorkflowText>
            <WorkflowText>
              バックエンドの「実装編 — スキーマ定義 + API仕様書の生成」で使った fileMatch
              モードとは使い分けています。
            </WorkflowText>
            <WorkflowLinkCard
              title="実装編 — スキーマ定義 + API仕様書の生成"
              description="fileMatchモードでのルール運用"
              path="/devdocs/handson/schema-api"
            />
            <WorkflowText>
              あちらはAPI仕様書ファイルを編集するたびにルールが自動で適用される設計でしたが、今回はYAMLを「一度生成したら基本的に触らない」想定のため、必要なときだけ呼び出す
              manual が適切です。
            </WorkflowText>
          </WorkflowTipBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション4: AIでapi_specs.yamlを生成する */}
      <WorkflowSection title="AIでapi_specs.yamlを生成する" id="section-4" number={4}>
        <WorkflowSection title="出力先ディレクトリを作成する" subNumber="4.1">
          <WorkflowText>まず docs/ ディレクトリを作成します。</WorkflowText>
          <WorkflowCodeBlock language="bash" code={`mkdir -p docs`} />
        </WorkflowSection>

        <WorkflowSection title="生成プロンプト" subNumber="4.2">
          <WorkflowText>Kiroのチャットに以下のプロンプトを入力します。</WorkflowText>

          <WorkflowChatBubble role="human">
            {`#api_specs_rule バックエンドプロジェクトのAPI仕様書を参照して、
docs/api_specs.yaml を生成してください。
全APIを1ファイルにまとめてOpenAPI 3.0形式で出力してください。`}
          </WorkflowChatBubble>

          <WorkflowInfoBox type="info">
            <Typography variant="body2" fontWeight={600}>
              ポイント
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              プロンプトの先頭に #api_specs_rule
              を付けることで、manualモードのSteeringルールを明示的に読み込んでいます。これを忘れるとルールが適用されず、AIが独自の判断でフォーマットを決めてしまいます。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>

        <WorkflowSection title="2回目以降のプロンプト" subNumber="4.3">
          <WorkflowText>
            初回は全APIを一括生成しますが、バックエンド側でAPIが追加・変更された場合に毎回全てを再生成する必要はありません。以下の2パターンで差分更新します。
          </WorkflowText>

          <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
            新規APIを追加する場合:
          </Typography>
          <WorkflowChatBubble role="human">
            {`#api_specs_rule バックエンドプロジェクトのAPI仕様書から
TODO削除API（DELETE /api/todos/{id}）を読み取り、
docs/api_specs.yaml に追加してください。
既存のAPI定義は変更しないでください。`}
          </WorkflowChatBubble>

          <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
            既存APIの仕様を最新化する場合:
          </Typography>
          <WorkflowChatBubble role="human">
            {`#api_specs_rule バックエンドプロジェクトのAPI仕様書を参照して、
docs/api_specs.yaml のTODO登録API（POST /api/todos）の定義を最新化してください。
他のAPIの定義は変更しないでください。`}
          </WorkflowChatBubble>

          <WorkflowInfoBox type="info">
            <Typography variant="body2" fontWeight={600}>
              ポイント
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              いずれの場合も「他のAPIは変更しない」を明示するのがコツです。指示しないとAIが親切心で他のAPIも更新してしまい、意図しない差分が発生することがあります。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>

        <WorkflowSection title="生成結果" subNumber="4.4">
          <WorkflowText>
            AIが docs/api_specs.yaml
            を生成します。バックエンドのAPI仕様書を全て読み取り、OpenAPI形式に変換した1ファイルが出力されます。
          </WorkflowText>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション5: 生成結果を確認する */}
      <WorkflowSection title="生成結果を確認する" id="section-5" number={5}>
        <WorkflowText>
          OpenAPIやSwaggerに馴染みがある方は、生成された docs/api_specs.yaml
          の内容を確認してみてください。
        </WorkflowText>

        <WorkflowSection title="確認のポイント" subNumber="5.1">
          <WorkflowList
            items={[
              '全APIのエンドポイントが含まれているか',
              'Request/Responseの型がバックエンドの仕様書と一致しているか',
              '不要なAPIや余計なフィールドが追加されていないか',
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="Swagger UIで確認する方法" subNumber="5.2">
          <WorkflowText>
            生成されたYAMLをブラウザで視覚的に確認したい場合は、Swagger Editorが便利です。
          </WorkflowText>

          <WorkflowList
            variant="ordered"
            items={[
              <Typography variant="body2" color="text.secondary" key="1">
                <Link href="https://editor.swagger.io/" target="_blank" rel="noopener">
                  Swagger Editor
                </Link>{' '}
                にアクセスする
              </Typography>,
              '生成された api_specs.yaml の内容をエディタに貼り付ける',
              '右側にAPI一覧がビジュアルで表示される',
            ]}
          />

          <WorkflowInfoBox type="important">
            <Typography variant="body2">
              <strong>重要: 実際の業務ではSwagger Editorに仕様書を貼り付けないでください。</strong>{' '}
              Swagger
              Editorは外部のWebサービスです。業務で扱うAPI仕様書（エンドポイント、リクエスト構造、認証方式等）を外部サービスに送信するとセキュリティインシデントになります。このハンズオンは個人の学習用プロジェクトなので問題ありませんが、業務では必ずローカルで起動するSwagger
              UIやVS Code拡張機能を使用してください。
            </Typography>
          </WorkflowInfoBox>

          <WorkflowInfoBox type="info">
            <Typography variant="body2" color="text.secondary">
              Swaggerの品質確認はこのハンズオンのスコープ外です。「読める人は確認してね」という位置づけなので、確認せずに次に進んでも問題ありません。もし明らかにAPIが足りない・型がおかしい等の問題があれば、もう一度プロンプトで修正を指示するか、api_specs_rule.mdの情報ソースのパスが正しいか確認してください。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション6: 現在のプロジェクト構成 */}
      <WorkflowSection title="現在のプロジェクト構成" id="section-6" number={6}>
        <WorkflowText>ここまでの作業で、プロジェクトは以下の状態になっています。</WorkflowText>

        <WorkflowDirectoryTree
          root="team_todo_front/"
          entries={[
            {
              name: '.kiro',
              color: '#1565c0',
              children: [
                {
                  name: 'steering',
                  children: [
                    { name: 'product.md' },
                    { name: 'tech.md' },
                    { name: 'structure.md' },
                    { name: 'api_specs_rule.md', desc: '今回作成', highlight: '#e65100' },
                  ],
                },
              ],
            },
            {
              name: 'docs',
              color: '#388e3c',
              children: [{ name: 'api_specs.yaml', desc: '今回生成', highlight: '#1976d2' }],
            },
            {
              name: 'node_modules',
              defaultOpen: false,
            },
            {
              name: 'public',
              defaultOpen: false,
            },
            {
              name: 'src',
              children: [{ name: 'App.css' }, { name: 'App.tsx' }, { name: 'main.tsx' }],
            },
            { name: 'HELPME.md' },
            { name: 'index.html' },
            { name: 'package.json' },
            { name: 'tsconfig.json' },
            { name: 'vite.config.ts' },
          ]}
        />
      </WorkflowSection>

      {/* 予告ボックス */}
      <WorkflowInfoBox type="preview">
        <Typography variant="body2" fontWeight={600}>
          次の記事: フロントエンド編 — 画面入出力定義書の作成
        </Typography>
        <WorkflowList
          items={[
            '画面入出力定義書の生成ルール（screen_io_rule.md）を設置する',
            'APIの仕様をもとに「各画面で何を表示し、何を入力させるか」を定義する',
            'PDCAサイクルで定義書の品質を改善する',
          ]}
        />
      </WorkflowInfoBox>

      {/* チェックリスト */}
      <WorkflowChecklist
        groups={[
          {
            title: 'チェックリスト',
            items: [
              '.kiro/steering/api_specs_rule.md を作成した',
              'api_specs_rule.md内のバックエンドプロジェクトのパスを自分の環境に合わせて書き換えた',
              'docs/api_specs.yaml がAIによって生成されている',
              '生成されたYAMLがOpenAPI 3.0形式である（ファイル冒頭に openapi: "3.0.0" 等がある）',
              'バックエンドの全APIがapi_specs.yamlに含まれている',
            ],
          },
        ]}
      />
    </HandsonArticleLayout>
  );
};

export default HandsonApiSpecs;
